// Image helpers for the evidence lane. Everything here is FAIL-SAFE: when the host cannot
// decode (jsdom, a broken file, an exotic format) the result is simply "unknown size, no
// thumbnail" — capture and album save never wait on, and never fail because of, this step.
const THUMBNAIL_MAX_EDGE = 360;

export interface ImageProbe {
  width: number | null;
  height: number | null;
  thumbnail: Blob | null;
}

export async function probeImage(blob: Blob): Promise<ImageProbe> {
  const none: ImageProbe = { width: null, height: null, thumbnail: null };
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return none;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(blob);
    const { width, height } = bitmap;
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(width, height, 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return { width, height, thumbnail: null };
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const thumbnail = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.82);
      } catch {
        resolve(null);
      }
    });
    return { width, height, thumbnail };
  } catch {
    return none;
  } finally {
    try {
      bitmap?.close();
    } catch {
      // nothing to release
    }
  }
}

// Downscaled (optionally square-cropped) JPEG for the report cover and the inspector avatar. FAIL-SAFE
// like probeImage: null when the host cannot decode (jsdom, a broken file) and the caller keeps the
// original bytes. Never upscales.
export interface ResizeOptions {
  maxEdge: number;
  quality?: number;
  // Centre-crop to a square before scaling (avatars).
  square?: boolean;
  // 2026-10-19: a company logo keeps its transparency as PNG; everything else is JPEG.
  type?: 'image/jpeg' | 'image/png';
}

export interface ResizedImage {
  blob: Blob;
  width: number;
  height: number;
}

export async function resizeImage(blob: Blob, options: ResizeOptions): Promise<ResizedImage | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(blob);
    const srcW = bitmap.width;
    const srcH = bitmap.height;
    if (srcW < 1 || srcH < 1) return null;
    const cropEdge = Math.min(srcW, srcH);
    const cropW = options.square ? cropEdge : srcW;
    const cropH = options.square ? cropEdge : srcH;
    const sx = options.square ? Math.round((srcW - cropEdge) / 2) : 0;
    const sy = options.square ? Math.round((srcH - cropEdge) / 2) : 0;
    const scale = Math.min(1, options.maxEdge / Math.max(cropW, cropH));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(cropW * scale));
    canvas.height = Math.max(1, Math.round(cropH * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, sx, sy, cropW, cropH, 0, 0, canvas.width, canvas.height);
    const out = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), options.type ?? 'image/jpeg', options.quality ?? 0.85);
      } catch {
        resolve(null);
      }
    });
    return out ? { blob: out, width: canvas.width, height: canvas.height } : null;
  } catch {
    return null;
  } finally {
    try {
      bitmap?.close();
    } catch {
      // nothing to release
    }
  }
}
